<template>
    <Overlay
        ref="modal"
        :propsData="{
            id: 'modal-add-to-bundle',
            headingId: 'modal-add-to-bundle-heading',
            reducedHeadingSpace: true
        }"
    >
        <template
            #Heading
        >
            <div class="sr-only">
                <h2 id="modal-add-to-bundle-heading">
                    {{ $root.config.translations.product_modal.overlay_title }}
                </h2>
            </div>
        </template>
        <template
            #Content
        >
            <div class="-mt-[4.3125rem]">
                <template v-if="isLightView">
                    <p
                        v-if="productData.vendor"
                        class="body-xs"
                    >
                        {{ productData.vendor }}
                    </p>
                </template>
                <template v-if="!isLightView">
                    <div
                        v-if="productData.image_url && productData.image_url_retina"
                        class="mb-5 large:mb-6"
                    >
                        <img
                            class="aspect-square object-cover"
                            :src="productData.image_url"
                            :srcset="`${productData.image_url_retina} 2x`"
                            width="260"
                            height="260"
                            :alt="productData.name"
                            loading="lazy"
                        >
                    </div>
                </template>
                <p
                    v-if="productData.name"
                    :class="{ 'sub-heading': !isLightView }"
                >
                    {{ productData.name }}
                </p>
                <template v-if="!isLightView">
                    <div
                        v-if="productData.short_description"
                        v-html="productData.short_description"
                        class="body-m mt-2"
                    ></div>
                </template>
                <div
                    class="font-bold body-m"
                    :class="{ 'mt-2': !isLightView }"
                >
                    <template v-if="currentVariantId">
                        {{ getPriceByVariantId }}{{ currencySymbol[0] }}
                    </template>
                    <template v-else>
                        <p v-if="productData.variants.length >= 2 && productData.price_min">
                            <span>{{ $root.config.translations.product_cards.from_price_before }}</span> <span>{{ productData.price_min }}</span>
                        </p>
                        <p
                            v-else-if="productData.compare_at_price && productData.price_currency"
                            class="flex flex-wrap items-center gap-1"
                        >
                            <s class="opacity-70">
                                {{ productData.compare_at_price }}
                            </s>
                            <span>
                                {{ productData.price_currency }}
                            </span>
                        </p>
                        <p v-else>
                            {{ productData.price_currency }}
                        </p>
                    </template>
                </div>
                <div
                    v-if="productData.variants.length >= 2"
                    :class="isLightView ? 'mt-4 medium:mt-[1.875rem]' : 'mt-3'"
                >
                    <p v-if="productData.options[0]" class="font-bold body-r mb-[0.375rem]">
                        {{ productData.options[0] }}
                    </p>
                    <div class="flex flex-wrap gap-3">
                        <div v-for="(variant, index) in productData.variants">
                            <button
                                class="body-r border border-inostime rounded-lg py-[0.625rem] px-5 transition-colors duration-200 hover:text-white hover:bg-inostime focus:text-white focus:bg-inostime [&.is-active]:text-white [&.is-active]:bg-inostime"
                                :class="{'is-active': currentVariantId === variant.id}"
                                :key="index"
                                @click.prevent="selectVariant(variant.id)"
                            >
                                {{ variant.title }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="quantity quantity--mini mt-4">
                    <button
                        type="button"
                        class="quantity__button"
                        :aria-label="$root.config.translations.product_cards.decrease_quantity"
                        @click="changeQuantity(productQuantity != 1 ? -1 : null)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor" />
                        </svg>
                    </button>
                    <span class="quantity__input">
                        {{ productQuantity }}
                    </span>
                    <button
                        type="button"
                        class="quantity__button"
                        :aria-label="$root.config.translations.product_cards.increase_quantity"
                        @click="changeQuantity(1)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor" />
                        </svg>
                    </button>
                </div>
                <div class="mt-5">
                    <template v-if="!productData.available">
                        <button type="button" class="button" disabled>
                            {{ $root.config.translations.product_cards.sold_out }}
                        </button>
                    </template>
                    <template v-else>
                        <button
                            type="button"
                            class="button"
                            :class="{'button--primary': currentVariantId}"
                            :disabled="productData.variants.length >= 2 && !currentVariantId"
                            @click="addToBundle(productData, productQuantity, currentVariantId ? currentVariantId : productData.id)"
                        >
                            {{ $root.config.translations.product_cards.add_to_bundle }}
                        </button>
                    </template>
                </div>
            </div>
        </template>
    </Overlay>
</template>

<script>
    import Overlay from '@/apps/bundle-builder/components/Overlay.vue';

    export default {
        components: {
            Overlay
        },
        emits: [
            'added-to-bundle',
            'quantity-changed',
            'variant-selected'
        ],
        props: {
            currencySymbol: {
                type: Array,
                default () {
                    return [];
                }
            },
            currentVariantId: {
                type: Number,
                default () {
                    return 0;
                }
            },
            isLightView: {
                type: Boolean,
                default () {
                    return false;
                }
            },
            productData: {
                type: Object,
                default () {
                    return {};
                }
            },
            productQuantity: {
                type: Number,
                default () {
                    return 0;
                }
            }
        },
        computed: {
            getPriceByVariantId () {
                // Find the product with the matching ID and return its price
                const productMatch = this.productData.variants.find(
                    (product) => product.id === this.currentVariantId
                );
                // Return the price if found, otherwise return a fallback value
                return productMatch ? (productMatch.price / 100).toFixed(2)  : `${this.$root.config.translations.product_modal.price_not_found}`;
            }
        },
        methods: {
            addToBundle (product, quantity, variantId) {
                this.$emit('added-to-bundle', product, quantity, variantId);
            },
            selectVariant (id) {
                this.$emit('variant-selected', id);
            },
            changeQuantity (qty) {
                this.$emit('quantity-changed', qty);
            }
        }
    }
</script>
